import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { clearGoogleCookies } from '@/lib/googleCookies';

/**
 * Cookie consent preferences interface
 * Strictly necessary cookies are always enabled and cannot be disabled
 */
export interface CookiePreferences {
  strictlyNecessary: boolean; // Always true, cannot be disabled
  analytics: boolean; // Requires consent
}

/**
 * Cookie consent status
 */
export interface CookieConsent {
  preferences: CookiePreferences;
  timestamp: number; // When consent was given/updated
  version: string; // Version of the consent system for future migrations
}

/**
 * Context interface
 */
interface CookieContextType {
  consent: CookieConsent | null;
  hasConsented: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  updatePreferences: (preferences: Partial<CookiePreferences>) => void;
  clearConsent: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  isPreferencesOpen: boolean;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

// Storage key for localStorage - scoped to byteet.nl domain
// Using a domain-specific prefix to avoid conflicts with Vercel toolbar or other tools
const CONSENT_STORAGE_KEY = 'byteet-nl-cookie-consent-v1';
const CONSENT_VERSION = '1.0.0';

// Cookie name for storing consent (as a backup/verification)
// This cookie will be cleared when user clears cookies in browser
const CONSENT_COOKIE_NAME = 'byteet-nl-consent';

// Default preferences (strictly necessary is always true)
const DEFAULT_PREFERENCES: CookiePreferences = {
  strictlyNecessary: true,
  analytics: false,
};

/**
 * Load consent from localStorage
 */
const loadConsent = (): CookieConsent | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const consent: CookieConsent = JSON.parse(stored);
    
    // Validate consent structure
    if (
      consent.preferences &&
      typeof consent.preferences.strictlyNecessary === 'boolean' &&
      typeof consent.preferences.analytics === 'boolean' &&
      consent.timestamp &&
      consent.version
    ) {
      // Ensure strictly necessary is always true
      consent.preferences.strictlyNecessary = true;
      return consent;
    }

    return null;
  } catch (error) {
    console.warn('Failed to load cookie consent:', error);
    return null;
  }
};

/**
 * Set a cookie to track consent (used to detect when cookies are cleared)
 */
const setConsentCookie = (consent: CookieConsent): void => {
  try {
    if (typeof document === 'undefined') {
      return;
    }

    // Set a cookie with the consent timestamp
    // This cookie will be cleared when user clears cookies, allowing us to detect it
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1); // 1 year expiration
    
    const cookieValue = `${consent.timestamp}-${consent.version}`;
    const domain = window.location.hostname === 'localhost' 
      ? 'localhost' 
      : '.byteet.nl'; // Use .byteet.nl for subdomain support
    
    // Only use Secure flag on HTTPS (required for Secure cookies)
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    
    document.cookie = `${CONSENT_COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/; domain=${domain}; SameSite=Lax${secureFlag}`;
  } catch (error) {
    console.warn('Failed to set consent cookie:', error);
  }
};

/**
 * Check if consent cookie exists (to detect if cookies were cleared)
 */
const hasConsentCookie = (): boolean => {
  try {
    if (typeof document === 'undefined') {
      return false;
    }
    
    return document.cookie.split(';').some(cookie => 
      cookie.trim().startsWith(`${CONSENT_COOKIE_NAME}=`)
    );
  } catch (error) {
    return false;
  }
};

/**
 * Clear the consent cookie
 */
const clearConsentCookie = (): void => {
  try {
    if (typeof document === 'undefined') {
      return;
    }
    
    const domains = [
      window.location.hostname === 'localhost' ? 'localhost' : '.byteet.nl',
      window.location.hostname,
    ];
    
    const isSecure = window.location.protocol === 'https:';
    const secureFlag = isSecure ? '; Secure' : '';
    
    domains.forEach(domain => {
      // Set to expire in the past
      document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=Lax${secureFlag}`;
      document.cookie = `${CONSENT_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secureFlag}`;
    });
  } catch (error) {
    console.warn('Failed to clear consent cookie:', error);
  }
};

/**
 * Save consent to localStorage and set a cookie for verification
 */
const saveConsent = (consent: CookieConsent): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    // Ensure strictly necessary is always true
    consent.preferences.strictlyNecessary = true;
    
    // Save to localStorage
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    
    // Also set a cookie as a backup/verification mechanism
    // This cookie will be cleared when user clears cookies in browser
    setConsentCookie(consent);
  } catch (error) {
    console.warn('Failed to save cookie consent:', error);
  }
};

/**
 * Clear consent from localStorage and cookie
 */
const clearConsentStorage = (): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    
    // Clear localStorage
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    
    // Clear consent cookie
    clearConsentCookie();
  } catch (error) {
    console.warn('Failed to clear cookie consent:', error);
  }
};

interface CookieProviderProps {
  children: ReactNode;
}

/**
 * Cookie Provider Component
 * Manages cookie consent state and provides it to the application
 */
export const CookieProvider: React.FC<CookieProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  // Load consent on mount
  useEffect(() => {
    // Check if cookies were cleared (consent cookie missing but localStorage has data)
    const loadedConsent = loadConsent();
    const cookieExists = hasConsentCookie();
    
    // If localStorage has consent but cookie doesn't exist, cookies were cleared
    // Reset consent to show banner again
    if (loadedConsent && !cookieExists) {
      clearConsentStorage();
      setConsent(null);
      return;
    }
    
    // If cookie exists but localStorage doesn't, sync them
    if (cookieExists && !loadedConsent) {
      // Cookie exists but localStorage is missing - this shouldn't happen normally
      // but if it does, clear the cookie to reset state
      clearConsentCookie();
      setConsent(null);
      return;
    }
    
    setConsent(loadedConsent);
    
    // Update consent mode based on loaded preferences
    if (loadedConsent) {
      import('@/lib/analytics')
        .then(({ updateConsent, initializeGA4, isGA4Initialized }) => {
          // Update consent mode
          updateConsent({ analytics: loadedConsent.preferences.analytics });
          
          // If consent was previously given and analytics is enabled, initialize it
          if (loadedConsent.preferences.analytics && !isGA4Initialized()) {
            initializeAnalytics();
          }
        })
        .catch((error) => {
          console.warn('Failed to update consent mode:', error);
        });
    }
  }, []);

  /**
   * Accept all cookies (enable all optional categories)
   */
  const acceptAll = () => {
    const newConsent: CookieConsent = {
      preferences: {
        strictlyNecessary: true,
        analytics: true,
      },
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    setConsent(newConsent);
    saveConsent(newConsent);
    setIsPreferencesOpen(false);

    // Update consent mode and initialize analytics if consent is given
    import('@/lib/analytics')
      .then(({ updateConsent, initializeGA4 }) => {
        updateConsent({ analytics: newConsent.preferences.analytics });
        
        if (newConsent.preferences.analytics) {
          initializeAnalytics();
        }
      })
      .catch((error) => {
        console.warn('Failed to update consent:', error);
      });
  };

  /**
   * Reject all optional cookies (only strictly necessary remain)
   */
  const rejectAll = () => {
    const newConsent: CookieConsent = {
      preferences: {
        strictlyNecessary: true,
        analytics: false,
      },
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    setConsent(newConsent);
    saveConsent(newConsent);
    setIsPreferencesOpen(false);

    // Update consent mode and disable analytics
    import('@/lib/analytics')
      .then(({ updateConsent }) => {
        updateConsent({ analytics: false });
        disableAnalytics();
      })
      .catch((error) => {
        console.warn('Failed to update consent:', error);
        disableAnalytics();
      });

    // Clear Google cookies when rejecting
    clearGoogleCookies();
  };

  /**
   * Update specific cookie preferences
   */
  const updatePreferences = (preferences: Partial<CookiePreferences>) => {
    const currentPreferences = consent?.preferences || DEFAULT_PREFERENCES;
    const newConsent: CookieConsent = {
      preferences: {
        ...currentPreferences,
        ...preferences,
        strictlyNecessary: true, // Always true
      },
      timestamp: Date.now(),
      version: CONSENT_VERSION,
    };

    setConsent(newConsent);
    saveConsent(newConsent);

    // Handle analytics based on new preference
    // Only update if the value actually changed to prevent multiple initializations
    const analyticsChanged = preferences.analytics !== undefined && 
                            preferences.analytics !== currentPreferences.analytics;
    
    if (analyticsChanged) {
      import('@/lib/analytics')
        .then(({ updateConsent, initializeGA4, isGA4Initialized }) => {
          // Update consent mode
          updateConsent({ analytics: preferences.analytics || false });
          
          if (preferences.analytics) {
            // Only initialize if not already initialized
            if (!isGA4Initialized()) {
              initializeAnalytics();
            }
          } else {
            disableAnalytics();
            // Clear Google cookies when disabling analytics
            clearGoogleCookies();
          }
        })
        .catch((error) => {
          console.warn('Failed to update consent:', error);
          if (preferences.analytics) {
            initializeAnalytics();
          } else {
            disableAnalytics();
            clearGoogleCookies();
          }
        });
    }
  };

  /**
   * Open preferences modal
   */
  const openPreferences = () => {
    setIsPreferencesOpen(true);
  };

  /**
   * Clear all consent data and reset to initial state
   * This will show the cookie banner again on next page load
   */
  const clearConsent = () => {
    // Clear from localStorage
    clearConsentStorage();
    
    // Reset state
    setConsent(null);
    setIsPreferencesOpen(false);
    
    // Disable analytics
    import('@/lib/analytics')
      .then(({ updateConsent, disableGA4 }) => {
        updateConsent({ analytics: false });
        disableGA4();
      })
      .catch((error) => {
        console.warn('Failed to disable analytics:', error);
      });
    
    // Clear Google cookies
    clearGoogleCookies();
    
    // Clear consent cookie
    clearConsentCookie();
    
    // Cookie consent cleared. Banner will show again on next page load.
  };

  /**
   * Close preferences modal
   */
  const closePreferences = () => {
    setIsPreferencesOpen(false);
  };

  const value: CookieContextType = {
    consent,
    hasConsented: consent !== null,
    acceptAll,
    rejectAll,
    updatePreferences,
    clearConsent,
    openPreferences,
    closePreferences,
    isPreferencesOpen,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};

/**
 * Hook to use cookie context
 */
export const useCookieConsent = (): CookieContextType => {
  const context = useContext(CookieContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
};

// Track if analytics has been initialized to prevent multiple initializations
let analyticsInitialized = false;

/**
 * Initialize Google Analytics 4 with Consent Mode
 * This function should only be called when analytics consent is given
 * 
 * Uses the analytics utility from @/lib/analytics
 */
function initializeAnalytics(): void {
  // Prevent multiple initializations
  if (analyticsInitialized) {
    return;
  }

  // Import dynamically to avoid issues if analytics module has errors
  import('@/lib/analytics')
    .then(({ initializeGA4, isGA4Initialized, updateConsent }) => {
      // Double-check if already initialized
      if (isGA4Initialized()) {
        analyticsInitialized = true;
        // Still update consent in case it changed
        updateConsent({ analytics: true });
        return;
      }
      
      // Update consent to granted before initializing
      updateConsent({ analytics: true });
      
      // Initialize GA4
      initializeGA4();
      analyticsInitialized = true;
    })
    .catch((error) => {
      console.warn('Failed to initialize analytics:', error);
      analyticsInitialized = false; // Reset on error so it can be retried
    });
}

/**
 * Disable Google Analytics
 * Updates consent to denied and removes GA4 scripts
 */
function disableAnalytics(): void {
  // Reset initialization flag
  analyticsInitialized = false;
  
  // Import dynamically to avoid issues if analytics module has errors
  import('@/lib/analytics')
    .then(({ disableGA4, updateConsent }) => {
      // Update consent to denied
      updateConsent({ analytics: false });
      
      // Disable GA4
      disableGA4();
      
      // Clear Google cookies
      clearGoogleCookies();
    })
    .catch((error) => {
      console.warn('Failed to disable analytics:', error);
      // Still try to clear cookies even if analytics module fails
      clearGoogleCookies();
    });
}

